'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { describeBulkDelete } from '@crm-src/lib/bulk-delete-report';
import { useNavigate } from "react-router-dom";
import { Plus, Search, Download, Eye, Edit, Trash2, CheckCircle, MoreHorizontal, FileText, Share2, Copy } from 'lucide-react';
import { InvoiceStatusBadge } from './invoice-status-badge';
import { collectIds, toggleId, allSelected, isIndeterminate as computeIndeterminate } from '@crm-src/lib/selection';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PageHeader } from '@/components/ui/page-header';
import { CreateWithAiButton } from '../../../otto-agent/CreateWithAiButton';
import { PageLoader } from '@/components/ui/page-loader';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@crm-src/hooks/useAuth';
import { useUsers } from '@crm-src/hooks/useUsers';
import { useInvoices } from '@crm-src/hooks/useInvoices';
import { useTableQuery } from '@crm-src/components/table/useTableQuery';
import { useDebouncedSearch } from '@crm-src/components/table/useDebouncedSearch';
import { useLatestRequest } from '@crm-src/components/table/useLatestRequest';
import { useTableSelection } from '@crm-src/components/table/useTableSelection';
import { buildTableParams } from '@crm-src/components/table/table-query';
import { SortableHeader } from '@crm-src/components/table/SortableHeader';
import { TableFilterBar } from '@crm-src/components/table/TableFilterBar';
import { TableEmptyState } from '@crm-src/components/table/TableEmptyState';
import { INVOICES_TABLE, type TableColumn } from '@crm-src/components/table/configs';
import { DocumentResellerCell } from '@crm-src/components/document-reseller-cell';
import { buildInvoiceFacetDefs, type InvoiceFacetKey } from '@crm-src/components/invoice-facets';
import { Pagination, paginationPropsFromMeta } from '@/components/ui/pagination';
import { StatsCard, ColoredDot } from "@crm-src/components/stats-card";
import { DeleteInvoiceModal } from "@crm-src/components/delete-invoice-modal";
import { BulkDeleteInvoiceModal } from "@crm-src/components/bulk-delete-invoice-modal";
import { MarkAsPaidModal } from "@crm-src/components/mark-as-paid-modal";
import { ShareDocumentModal } from "@crm-src/components/share-document-modal";
import { InvoiceCreationMethodSelector } from "@crm-src/components/invoice-creation-method-selector";
import { HaveInvoiceForm } from "@crm-src/components/have-invoice-form";
import api from '@crm-src/lib/api';
import { formatCurrency } from '@crm-src/lib/utils';
import { formatDate } from '@crm-src/lib/date-utils';
import type { Invoice, InvoiceListFilters } from '@crm-src/lib/store/invoicesSlice';
import { downloadDocumentPdf } from '@crm-src/lib/document-pdf';
import { InvoiceTabs } from '@crm-src/components/invoice-tabs';
import { InvoiceDetailsDialog } from '@crm-src/components/invoices/invoice-details-dialog';


interface InvoiceDetails extends Invoice {
  client_invoice_number?: string;
  purchase_order_number?: string;
  client_phone?: string;
  client_address?: string;
  client_company?: string;
  client_tax_id?: string;
  description?: string;
  payment_terms_days?: number;
  payment_terms?: string;
  terms_and_conditions?: string;
  payment_method?: string;
  payment_reference?: string;
  is_recurring?: boolean;
  recurring_frequency?: 'monthly' | 'quarterly' | 'yearly';
  file_name?: string;
  file_type?: string;
  file_size?: number;
  payment_percentage?: number;
  overdue?: boolean;
  archived?: boolean;
  currency?: string;
  discount_amount?: number;
  market_discount?: number;
  reseller_discount?: number;
  additional_discount_amount?: number;
  additional_discount_percentage?: number;
  paid_date?: string;
  
  // Generated invoice fields
  subject?: string;
  po_number?: string;
  sales_person?: string;
  terms?: string;
  
  // Bill To fields
  bill_to_company?: string;
  bill_to_address?: string;
  bill_to_city?: string;
  bill_to_state?: string;
  bill_to_zip?: string;
  bill_to_country?: string;
  
  // Ship To fields
  ship_to_company?: string;
  ship_to_address?: string;
  ship_to_city?: string;
  ship_to_state?: string;
  ship_to_zip?: string;
  ship_to_country?: string;
  
  // Products
  products?: Array<{
    id?: string;
    partNumber?: string;
    description: string;
    seats: number;
    chargePerSeat: number;
    amount: number;
  }>;
  
  // Additional totals
  sub_total?: number;
  balance_due?: number;
}

interface Statistics {
  total_count: number;
  draft_count: number;
  sent_count: number;
  paid_count: number;
  overdue_count: number;
  total_outstanding: number;
  total_overdue: number;
  total_paid_this_month: number;
}

/** Rows per page. Matches every other CRM list. */
const PER_PAGE = 25;

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    invoices,
    isLoading,
    error,
    stats,
    getInvoices,
    createNewInvoice,
    updateExistingInvoice,
    removeInvoice,
    removeBulkInvoices,
    uploadFile,
    markAsPaid,
    markAsUnpaid,
    getInvoiceStats,
    clearInvoicesError,
  } = useInvoices();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [invoiceCreationMethod, setInvoiceCreationMethod] = useState<'selection' | 'generate' | 'have-invoice'>('selection');

  const [showMarkAsPaidDialog, setShowMarkAsPaidDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceDetails | null>(null);
  const [invoiceToShare, setInvoiceToShare] = useState<InvoiceDetails | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Annotated, not inferred: a bare `: TableConfig` (or an inferred key list)
  // widens `facetKeys` to `string[]`, at which point a misspelled facet key
  // stops being a compile error.
  //
  // This REPLACES two dead `useState`s (`statusFilter` / `paymentStatusFilter`)
  // that were declared here and never read by anything — the page has never had
  // a working filter control.
  const facetKeys = useMemo<readonly InvoiceFacetKey[]>(() => INVOICES_TABLE.facetKeys, []);
  const table = useTableQuery({
    defaultSort: INVOICES_TABLE.defaultSort,
    defaultDir: INVOICES_TABLE.defaultDir,
    facetKeys,
  });

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset. Search
  // text is deliberately NOT in the URL; sort, facets and page are.
  const search = useDebouncedSearch(table.setPage);

  const { users } = useUsers();
  // `facetKeys` is passed, not defaulted: `TableFilterBar` renders whatever
  // `facetDefs` it is handed and does not intersect them with the page's
  // declared keys, so the config's list has to reach the BUILDER for a
  // dropped facet to actually disappear. See INVOICES_TABLE.
  const facetDefs = useMemo(() => buildInvoiceFacetDefs(users, facetKeys), [users, facetKeys]);
  // `as const` config entries carry no `align`, so the array needs the explicit
  // element type rather than the literal one TypeScript infers.
  const columns = useMemo<readonly TableColumn[]>(() => INVOICES_TABLE.columns, []);

  // Pagination state (what the LAST response reported — the requested page
  // lives in the URL via `table.page`).
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: PER_PAGE,
    total_count: 0,
    total_pages: 1
  });

  const { user } = useAuth();

  // Form state is now managed within the components



  // These states are now managed within the components



  const [selectedInvoiceForDetails, setSelectedInvoiceForDetails] = useState<Invoice | null>(null);
  const [selectedInvoiceForPaidDate, setSelectedInvoiceForPaidDate] = useState<Invoice | null>(null);
  
  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL) and the search term from the
  // debounce above. `document_type` is NOT a facet — it selects WHICH page this
  // is — so it rides alongside rather than through `buildTableParams`.
  const listParams = useMemo<InvoiceListFilters>(() => ({
    ...buildTableParams(
      { sort: table.sort, dir: table.dir },
      table.facets,
      table.page,
      PER_PAGE,
      search.search,
      // While a search is running and no column has been picked, this omits
      // `sort`/`dir` so the crm-api ranks by search relevance instead. See
      // buildTableParams.
      table.sortExplicit,
    ),
    document_type: 'invoice' as const,
  }), [table.sort, table.dir, table.sortExplicit, table.facets, table.page, search.search]);

  // Bulk selection, cleared whenever `listParams` describes a different result
  // set — otherwise "Delete 3" stays armed against rows a new filter has taken
  // off screen. Holds IDS rather than rows so the existing Set helpers
  // (`collectIds` / `toggleId`) keep working unchanged. See useTableSelection.
  const {
    selected: selectedIdList,
    setSelected: setSelectedIdList,
    clear: clearSelection,
  } = useTableSelection<string>(listParams);
  const selectedInvoices = useMemo(() => new Set(selectedIdList), [selectedIdList]);
  const setSelectedInvoices = useCallback(
    (next: Set<string>) => setSelectedIdList([...next]),
    [setSelectedIdList],
  );
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Per-row actions menu anchor (keyed by invoice id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);

  // Drops a slow earlier response that would otherwise land after a faster
  // later one — easy to hit now that a header or facet click fires immediately.
  const beginRequest = useLatestRequest();

  const loadInvoices = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      const response = await getInvoices(listParams);
      if (!isCurrent()) return;

      // Update pagination state from API response
      if (response?.pagination) {
        setPagination(prev => ({
          ...prev,
          current_page: response.pagination.current_page,
          total_pages: response.pagination.total_pages,
          total_count: response.pagination.total_count,
          per_page: response.pagination.per_page
        }));
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error loading invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoices',
        variant: 'destructive'
      });
    }
  }, [getInvoices, listParams, beginRequest]);

  // Load invoices on mount and whenever the query (sort / facets / page /
  // search) changes.
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    // Load stats on mount - filter for invoices only
    getInvoiceStats({ document_type: 'invoice' });
  }, []);


  const handleCreateInvoice = async (invoiceData: any, file?: File) => {
    try {
      setIsCreating(true);
      
      const createdInvoice = await createNewInvoice(invoiceData, file);
      
      // Success message based on whether file was included
      if (file) {
        toast({
          title: 'Success',
          description: 'Invoice created and file uploaded successfully. File automatically saved to customer file manager.'
        });
      } else {
        toast({
          title: 'Success',
          description: 'Invoice created successfully'
        });
      }

      setShowCreateDialog(false);
      resetForm();
      
      // Refresh stats - filter for invoices only
      getInvoiceStats({ document_type: 'invoice' });
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to create invoice',
        variant: 'destructive'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setInvoiceCreationMethod('selection');
    
    // Form state is now managed within the components
    // No need to reset individual form fields here
  };

  // Bulk selection (pure Set logic lives in crm/lib/selection.ts)
  const isAllSelected = allSelected(selectedInvoices.size, invoices?.length || 0);
  const isIndeterminate = computeIndeterminate(selectedInvoices.size, invoices?.length || 0);

  const handleSelectAll = (checked: boolean) => {
    setSelectedInvoices(checked && invoices ? collectIds(invoices, (invoice) => invoice.id.toString()) : new Set());
  };

  const handleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices(toggleId(selectedInvoices, invoiceId));
  };

  const getSelectedInvoiceObjects = (): InvoiceDetails[] => {
    if (!invoices) return [];
    return invoices
      .filter(invoice => selectedInvoices.has(invoice.id.toString()))
      .map(invoice => invoice as InvoiceDetails);
  };

  const handleBulkDelete = async (invoiceIds: string[]) => {
    try {
      setIsBulkDeleting(true);
      
      // Convert string IDs to numbers for the API
      const numericIds = invoiceIds.map(id => parseInt(id, 10));
      const result = await removeBulkInvoices(numericIds);
      
      // What crm-api actually did — a reseller's mirrored quote is left alone
      // and named, rather than counted as deleted (2026-09-15).
      const report = describeBulkDelete(result.response, numericIds, "invoice");
      toast({ title: report.title, description: report.description, ...(report.variant ? { variant: report.variant } : {}) });
      
      // Clear selection and refresh data
      clearSelection();
      await loadInvoices();
      await getInvoiceStats();
      
    } catch (error) {
      console.error('Error bulk deleting invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete some or all invoices',
        variant: 'destructive'
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handlePageChange = (page: number) => {
    table.setPage(page);
  };


  const loadInvoiceDetails = async (id: number) => {
    try {
      const response = await api.get(`/invoices/${id}`);
      const invoiceDetails = response.data.data || response.data.invoice || response.data;
      setSelectedInvoiceForDetails(invoiceDetails);
      
      // Customer deal selection is now handled within the component
    } catch (error) {
      console.error('Error loading invoice details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice details',
        variant: 'destructive'
      });
    }
  };

  const markAsSent = async (id: number) => {
    try {
      await api.post(`/invoices/${id}/mark_as_sent`);
      toast({
        title: 'Success',
        description: 'Invoice marked as sent'
      });
      loadInvoices();
    } catch (error) {
      console.error('Error marking invoice as sent:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark invoice as sent',
        variant: 'destructive'
      });
    }
  };



  const handleMarkAsPaidConfirm = async (invoiceId: string, paidDate: string) => {
    setIsMarkingAsPaid(true);
    try {
      await markAsPaid(parseInt(invoiceId), 0, paidDate); // Note: amount is not used by markAsPaid
      toast({
        title: 'Success',
        description: 'Invoice marked as paid'
      });
      setShowMarkAsPaidDialog(false);
      setSelectedInvoiceForPaidDate(null);
      setShowDetailDialog(false); // Close details dialog if open
      loadInvoices(); // Refresh invoices list
      getInvoiceStats(); // Refresh stats
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark invoice as paid',
        variant: 'destructive'
      });
    } finally {
      setIsMarkingAsPaid(false);
    }
  };

  const openMarkAsPaidDialog = (invoice: Invoice) => {
    setSelectedInvoiceForPaidDate(invoice);
    setShowMarkAsPaidDialog(true);
  };

  const handleMarkAsUnpaid = async (invoice: Invoice) => {
    try {
      await markAsUnpaid(invoice.id);
      toast({
        title: 'Success',
        description: 'Invoice marked as unpaid'
      });
      setShowDetailDialog(false);
      loadInvoices(); // Refresh invoices list
      getInvoiceStats(); // Refresh stats
    } catch (error) {
      console.error('Error marking invoice as unpaid:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark invoice as unpaid',
        variant: 'destructive'
      });
    }
  };

  // The one generator's PDF, fetched from crm-api — see lib/document-pdf.ts.
  const handleDownloadInvoice = async (invoice: Invoice | InvoiceDetails) => {
    try {
      await downloadDocumentPdf(invoice.id, { documentType: 'invoice', number: invoice.invoice_number });
      toast({
        title: 'Success',
        description: 'Invoice PDF downloaded successfully'
      });
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to download invoice PDF',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteInvoiceClick = (invoice: InvoiceDetails) => {
    setInvoiceToDelete(invoice);
    setShowDeleteDialog(true);
  };

  const handleShareInvoiceClick = (invoice: InvoiceDetails) => {
    setInvoiceToShare(invoice);
    setShowShareDialog(true);
  };

  const handleShareModalClose = () => {
    setShowShareDialog(false);
    setInvoiceToShare(null);
  };

  const handleEditInvoiceClick = (invoice: InvoiceDetails) => {
    // Navigate to edit page with invoice ID
    navigate(`/crm/invoices/generate?invoiceId=${invoice.id}`);
  };

  const handleDuplicateInvoiceClick = (invoice: InvoiceDetails) => {
    // Navigate to generate page with duplicateFrom parameter
    // This will load all invoice data EXCEPT the invoice number (which will be auto-generated)
    navigate(`/crm/invoices/generate?duplicateFrom=${invoice.id}`);
  };

  const handleDeleteInvoiceConfirm = async () => {
    if (!invoiceToDelete) return;
    
    try {
      setIsDeleting(true);
      await removeInvoice(invoiceToDelete.id);
      
      toast({
        title: 'Success',
        description: 'Invoice deleted successfully'
      });
      
      // Close modal and refresh data
      setShowDeleteDialog(false);
      setInvoiceToDelete(null);
      getInvoiceStats(); // Refresh stats
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete invoice',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteModalClose = () => {
    if (isDeleting) return; // Don't allow closing while deleting
    setShowDeleteDialog(false);
    setInvoiceToDelete(null);
  };

  // Helper function to determine if a document is a quote
  const isQuote = (invoice: any) => {
    // Check document_type field first
    const documentType = (invoice as any)?.document_type || 'invoice';
    if (documentType === 'quote') return true;
    
    // Fallback: check invoice number pattern (quotes start at 500)
    if (invoice?.invoice_number) {
      const parts = invoice.invoice_number.split('-');
      if (parts.length === 3) {
        const lastPart = parseInt(parts[2]);
        return lastPart >= 500; // Quote numbers are 500+
      }
    }
    return false;
  };

  if (isLoading && !stats) {
    return <PageLoader />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Tabs Navigation */}
      <InvoiceTabs />

      {/* Header */}
      <PageHeader
        title="All Invoices"
        description="View, create, and manage all your invoices"
        actions={
          <>
            <CreateWithAiButton skill="create_invoice" />
            <Button variant="contained" onClick={() => setShowCreateDialog(true)} startIcon={<Plus size={16} />}>
              Create Invoice
            </Button>
            <Dialog
              open={showCreateDialog}
              onClose={() => { resetForm(); setShowCreateDialog(false); }}
              maxWidth="md"
              fullWidth
            >
              {/* Initial Selection */}
              {invoiceCreationMethod === 'selection' && (
                <>
                  <DialogTitle>
                    Create New Invoice
                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                      Choose how you want to create your invoice
                    </Typography>
                  </DialogTitle>
                  <DialogContent>
                    <InvoiceCreationMethodSelector
                      onSelectMethod={setInvoiceCreationMethod}
                    />

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button variant="outlined" color="inherit" onClick={() => setShowCreateDialog(false)}>
                        Cancel
                      </Button>
                    </Box>
                  </DialogContent>
                </>
              )}

              {/* Generate Invoice is now handled on a separate page */}

              {/* I Have Invoice - Form */}
              {invoiceCreationMethod === 'have-invoice' && (
                <DialogContent>
                  <HaveInvoiceForm
                    onBack={() => setInvoiceCreationMethod('selection')}
                    onCancel={() => setShowCreateDialog(false)}
                    onSubmit={handleCreateInvoice}
                    isCreating={isCreating}
                  />
                </DialogContent>
              )}
            </Dialog>
          </>
        }
      />

      {/* Stats Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
        <StatsCard
          title="Total Invoices"
          value={stats?.total_invoices || 0}
          icon={FileText}
          iconColor="text-blue-600"
        />
        <StatsCard
          title="Overdue"
          value={stats?.overdue_invoices || 0}
          valueColor="text-red-600"
          customIcon={<ColoredDot color="bg-red-100" />}
        />
        <StatsCard
          title="Pending"
          value={(stats?.unpaid_invoices || 0) - (stats?.overdue_invoices || 0)}
          valueColor="text-yellow-600"
          customIcon={<ColoredDot color="bg-yellow-100" />}
        />
        <StatsCard
          title="Paid"
          value={stats?.paid_invoices || 0}
          valueColor="text-green-600"
          customIcon={<ColoredDot color="bg-green-100" />}
        />
      </Box>

      {/* Invoices Table */}
      <Card>
        <CardHeader
          // The header now hosts a whole filter row, not one field: render it
          // as a <div> so the Boxes/Selects inside are not nested in a <span>.
          titleTypographyProps={{ component: "div" }}
          title={
            // The search box is WRAPPED, not replaced — same field, same 500ms
            // debounce; the facets simply sit beside it.
            <TableFilterBar
              facetDefs={facetDefs}
              values={table.facets}
              onChange={table.setFacet}
              onClearAll={table.clearFacets}
              searchSlot={
                <TextField
                  size="small"
                  placeholder="Search by invoice number, deal name, client name, company..."
                  value={search.input}
                  onChange={(e) => search.setInput(e.target.value)}
                  sx={{ width: { xs: "100%", sm: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ display: "flex", color: search.isSearching ? "primary.main" : "text.disabled" }}><Search size={16} /></Box>
                      </InputAdornment>
                    ),
                    ...(search.isSearching ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography component="span" sx={{ fontSize: 14, color: "primary.main" }}>Searching...</Typography>
                        </InputAdornment>
                      ),
                    } : {}),
                  }}
                />
              }
            />
          }
        />
        <CardContent>
          {/* Bulk Actions Toolbar */}
          {selectedInvoices.size > 0 && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: (t) => alpha(t.palette.primary.main, 0.08), border: 1, borderColor: (t) => alpha(t.palette.primary.main, 0.3), borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "primary.dark" }}>
                    {selectedInvoices.size} invoice{selectedInvoices.size > 1 ? 's' : ''} selected
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={clearSelection}
                  >
                    Clear Selection
                  </Button>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => setShowBulkDeleteDialog(true)}
                    disabled={isBulkDeleting}
                    startIcon={<Trash2 size={16} />}
                  >
                    Delete Selected
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }}>
                    <Checkbox
                      size="small"
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      inputProps={{ 'aria-label': 'Select all invoices' }}
                    />
                  </TableCell>
                  {/* The eight DATA columns, in `INVOICES_TABLE` order —
                      which is the order the body cells below are written in,
                      so the two must be edited together (a header with no
                      body cell shifts every later column — see the Reseller
                      cell below). Each sorts on the
                      server key its own cell renders (Customer on the company
                      line, Status on payment_status, and so on). The checkbox
                      and Actions cells either side stay plain <TableCell>s:
                      neither maps to a server column, so neither can sort. */}
                  {columns.map((col) => (
                    <SortableHeader
                      key={col.key}
                      column={col.key}
                      label={col.label}
                      sort={table.sort}
                      dir={table.dir}
                      onSort={table.toggleSort}
                      {...(col.align ? { align: col.align } : {})}
                    />
                  ))}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(INVOICES_TABLE.columns.length + 2)].map((_, j) => (
                        <TableCell key={j}>
                          {j === 0 ? (
                            <Skeleton variant="rounded" sx={{ height: 16, width: 16 }} />
                          ) : (
                            <Skeleton variant="rounded" sx={{ height: 16, width: 80 }} />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : !invoices || invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={INVOICES_TABLE.columns.length + 2} sx={{ textAlign: "center", py: 4 }}>
                      {/* Names whichever narrowing actually emptied the list:
                          filtering to nothing used to claim the tenant had no
                          invoices at all, and offered no way back. */}
                      <TableEmptyState
                        entityNoun="invoices"
                        hasSearch={search.input.trim().length > 0}
                        activeFilterCount={table.activeFilterCount}
                        onClearFilters={table.clearFacets}
                        emptyTitle="No invoices found"
                        emptyBody="Create your first invoice to get started"
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  (invoices || []).filter(invoice => invoice && invoice.id).map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      sx={{ "&:hover": { bgcolor: "action.hover" } }}
                    >
                      <TableCell sx={{ width: 48 }} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedInvoices.has(invoice.id.toString())}
                          onChange={() => handleSelectInvoice(invoice.id.toString())}
                          inputProps={{ 'aria-label': `Select invoice ${invoice.invoice_number}` }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 500, cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/crm/invoices/${invoice.id}`);
                        }}
                      >
                        <Typography>{invoice.invoice_number || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer", maxWidth: 200 }}
                        onClick={() => {
                          navigate(`/crm/invoices/${invoice.id}`);
                        }}
                      >
                        <Box sx={{ maxWidth: 200 }}>
                          <Typography sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={invoice.customer_company_name || 'N/A'}>{invoice.customer_company_name || 'N/A'}</Typography>
                          <Typography sx={{ fontSize: 14, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={invoice.customer_name || 'N/A'}>{invoice.customer_name || 'N/A'}</Typography>
                        </Box>
                      </TableCell>
                      {/* RESELLER — the ONE cell both invoice tables share
                          (components/document-reseller-cell.tsx). The header
                          above comes from INVOICES_TABLE; a header with no
                          body cell here shifts every later value one column
                          left (that was v0.1.102's invoices page). */}
                      <DocumentResellerCell
                        reseller={invoice.bill_to_reseller}
                        locked={invoice.locked}
                        onClick={() => navigate(`/crm/invoices/${invoice.id}`)}
                      />
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/crm/invoices/${invoice.id}`);
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 500 }}>{formatCurrency(invoice.total_amount || 0)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/crm/invoices/${invoice.id}`);
                        }}
                      >
                        <InvoiceStatusBadge status={invoice.payment_status || 'unpaid'} isQuote={isQuote(invoice)} overdue={!!invoice.overdue} />
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/crm/invoices/${invoice.id}`);
                        }}
                      >
                        <Typography {...(invoice.payment_status === 'overdue' ? { sx: { color: "error.main", fontWeight: 500 } } : {})}>
                          {formatDate(invoice.due_date)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/crm/invoices/${invoice.id}`);
                        }}
                      >
                        <Typography sx={invoice.paid_date ? { color: "success.main", fontWeight: 500 } : { color: "text.disabled" }}>
                          {invoice.paid_date ? formatDate(invoice.paid_date) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/crm/invoices/${invoice.id}`);
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography component="span" sx={{ fontSize: 14 }}>{formatDate(invoice.created_at)}</Typography>
                          {invoice.created_by && (
                            <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                              {invoice.created_by.full_name}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" sx={{ width: 32, height: 32 }} aria-label="Open menu" onClick={(e) => setMenuAnchor({ el: e.currentTarget, id: invoice.id.toString() })}>
                          <MoreHorizontal size={16} />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchor?.id === invoice.id.toString() ? menuAnchor.el : null}
                          open={menuAnchor?.id === invoice.id.toString()}
                          onClose={() => setMenuAnchor(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
                          <MenuItem onClick={() => { setMenuAnchor(null); navigate(`/crm/invoices/${invoice.id}`); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                            View Details
                          </MenuItem>
                          <MenuItem onClick={() => { setMenuAnchor(null); handleEditInvoiceClick(invoice); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                            Edit Invoice
                          </MenuItem>
                          <MenuItem onClick={() => { setMenuAnchor(null); handleDuplicateInvoiceClick(invoice); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Copy size={16} /></Box>
                            Duplicate
                          </MenuItem>
                          {invoice.payment_status !== 'paid' && !isQuote(invoice) && (
                            <MenuItem onClick={() => { setMenuAnchor(null); openMarkAsPaidDialog(invoice as Invoice); }}>
                              <Box sx={{ mr: 1, display: "flex" }}><CheckCircle size={16} /></Box>
                              Mark as Paid
                            </MenuItem>
                          )}
                          <Divider />
                          <MenuItem onClick={() => { setMenuAnchor(null); handleShareInvoiceClick(invoice); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Share2 size={16} /></Box>
                            Share
                          </MenuItem>
                          <MenuItem onClick={() => { setMenuAnchor(null); handleDownloadInvoice(invoice); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Download size={16} /></Box>
                            Download PDF
                          </MenuItem>
                          <Divider />
                          <MenuItem onClick={() => { setMenuAnchor(null); handleDeleteInvoiceClick(invoice); }} sx={{ color: "error.main" }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                            Delete
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          {/* Pagination */}
          <Pagination
            {...paginationPropsFromMeta(pagination)}
            entityNoun="invoices"
            hideIfSinglePage
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Invoice Details Dialog */}
      <InvoiceDetailsDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        invoice={selectedInvoiceForDetails}
        isQuote={isQuote}
        onMarkAsPaid={openMarkAsPaidDialog}
        onMarkAsUnpaid={handleMarkAsUnpaid}
        onDownload={handleDownloadInvoice}
      />


      
      {/* Create Customer Deal Dialog is now handled within HaveInvoiceForm component */}

      {/* Mark as Paid Modal */}
      <MarkAsPaidModal
        isOpen={showMarkAsPaidDialog}
        onClose={() => {
          setShowMarkAsPaidDialog(false);
          setSelectedInvoiceForPaidDate(null);
        }}
        invoice={selectedInvoiceForPaidDate}
        onConfirm={handleMarkAsPaidConfirm}
        isLoading={isMarkingAsPaid}
      />

      {/* Delete Invoice Modal */}
      <DeleteInvoiceModal
        isOpen={showDeleteDialog}
        onClose={handleDeleteModalClose}
        invoiceId={invoiceToDelete?.id?.toString() || null}
        onConfirmDelete={(invoiceId) => handleDeleteInvoiceConfirm()}
      />

      {/* Bulk Delete Invoice Modal */}
      <BulkDeleteInvoiceModal
        isOpen={showBulkDeleteDialog}
        onClose={() => setShowBulkDeleteDialog(false)}
        selectedInvoices={getSelectedInvoiceObjects()}
        onConfirmDelete={handleBulkDelete}
      />

      {/* Share Invoice Modal */}
      <ShareDocumentModal
        isOpen={showShareDialog}
        onClose={handleShareModalClose}
        documentId={invoiceToShare?.id?.toString() || null}
        documentType={isQuote(invoiceToShare) ? 'quote' : 'invoice'}
        documentNumber={invoiceToShare?.invoice_number}
      />
    </Box>
  );
};

export default InvoicesPage;